import analyticsConfig from '@/config/analytics';

export type EventMap = {
  // Notes
  'note.created': {};
  'note.updated': { fields: string[] };
  'note.deleted': {};
  'note.content_updated': { lineCount: number };

  // Editor
  'editor.tag_selected': { tagName: string };
  'editor.paste_import': { count: number; titled: boolean };

  // Attachments
  'attachment.added': { source: 'picker' | 'paste' | 'drop'; count: number; target: 'row' | 'draft' };
  'attachment.deleted': {};
  'attachment.viewed_fullscreen': {};
  'attachment.upload_failed': { reason: string };

  // Sharing
  'share.button_clicked': { diaryId: string; entryCount: number; hasTitle: boolean };
  'share.url_copied': { diaryId: string };
  'share.copied_as_text': { diaryId: string; contentLength: number };
  'share.image_downloaded': { diaryId: string; entryCount: number };
  'share.diary_viewed': { contentLength: number; hasTitle: boolean };
  'share.app_link_clicked': { contentLength: number };
  'share.note_viewed': { noteId: string; referrer: string };
  'share.note_link_clicked': { source: string };

  // Migration
  'migration.schema_complete': { version: number; noteCount: number; entryCount: number };
  'migration.complete': { noteCount: number; entryCount: number };

  // App Store
  'appstore_banner_clicked': {};

  // Waitlist
  'waitlist.signup_submitted': { platform: 'android' | 'updates' };

  // Blog
  'blog_article_viewed': {
    slug: string;
    referrer: string;
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
  };
  'blog_cta_clicked': {
    slug: string;
    cta_position: 'inline' | 'footer';
    destination: string;
  };

  // Settings
  'settings.reverse_order_toggled': { enabled: boolean };
  'settings.timestamp_moment_changed': { value: 'onReturn' | 'onStartTyping' };
  'settings.show_milliseconds_toggled': { enabled: boolean };
  'settings.sign_out': Record<string, never>;
  'settings.restore_pro_clicked': Record<string, never>;
  'settings.upgrade_clicked': Record<string, never>;
  'settings.manage_subscription_clicked': Record<string, never>;
};

type EventProperties = Record<string, any>;

type MixpanelInstance = {
  init: (token: string, options?: Record<string, any>) => void;
  track: (eventName: string, properties?: EventProperties) => void;
  identify: (userId: string) => void;
  people: { set: (traits: Record<string, any>) => void };
  reset: () => void;
};

class AnalyticsService {
  private initialized = false;
  private mixpanel: MixpanelInstance | null = null;
  private pendingEvents: Array<{ name: string; properties?: EventProperties }> = [];

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (analyticsConfig.mixpanel.enabled) {
      const { default: mixpanel } = await import('mixpanel-browser');
      mixpanel.init(
        analyticsConfig.mixpanel.token,
        analyticsConfig.mixpanel.options
      );
      this.mixpanel = mixpanel;
      console.log('📊 Mixpanel initialized');

      // Flush any events queued before init completed
      for (const event of this.pendingEvents) {
        mixpanel.track(event.name, event.properties);
      }
      this.pendingEvents = [];
    }

    this.initialized = true;
  }

  track<K extends keyof EventMap>(event: K, properties: EventMap[K]): void {
    if (import.meta.env.DEV) {
      console.log(`📊 Event: ${event}`, properties);
    }

    if (this.mixpanel) {
      this.mixpanel.track(event, properties as Record<string, unknown>);
    } else if (analyticsConfig.mixpanel.enabled) {
      this.pendingEvents.push({ name: event, properties: properties as EventProperties });
    }
  }

  trackEvent(eventName: string, properties?: EventProperties): void {
    if (import.meta.env.DEV) {
      console.log(`📊 Event: ${eventName}`, properties || {});
    }

    if (this.mixpanel) {
      this.mixpanel.track(eventName, properties);
    } else if (analyticsConfig.mixpanel.enabled) {
      this.pendingEvents.push({ name: eventName, properties });
    }
  }

  trackError(error: Error, context?: Record<string, any>): void {
    if (import.meta.env.DEV) {
      console.error('📊 Error:', error, context || {});
    }

    if (this.mixpanel) {
      this.mixpanel.track('Error', {
        message: error.message,
        stack: error.stack,
        ...context,
      });
    }
  }

  identify(userId: string, traits?: Record<string, string>): void {
    if (this.mixpanel) {
      this.mixpanel.identify(userId);
      if (traits) {
        this.mixpanel.people.set(traits);
      }
    }
  }

  reset(): void {
    if (this.mixpanel) {
      this.mixpanel.reset();
    }
  }
}

const analytics = new AnalyticsService();
export default analytics;
